import { normalizeCogniveId } from './optimizerIdentityHelpers';

const nonemptyString = (value) => (
    typeof value === 'string' && value.trim() ? value.trim() : null
);

function responseDetail(value) {
    const detail = value?.detail;
    if (typeof detail === 'object' && detail !== null) {
        return nonemptyString(detail.message);
    }
    return nonemptyString(detail);
}

function responseCode(value) {
    return nonemptyString(value?.detail?.code) ?? nonemptyString(value?.code);
}

function errorPayload(value) {
    const message = nonemptyString(value?.message);
    if (!message) return null;
    const separator = message.lastIndexOf(' - ');
    if (separator < 0) return null;
    try {
        return JSON.parse(message.slice(separator + 3));
    } catch (_error) {
        return null;
    }
}

export function optimizerErrorCode(value) {
    return responseCode(value) ?? responseCode(errorPayload(value));
}

export function optimizerErrorMessage(value, fallback = 'Optimizer request failed.') {
    const detail = responseDetail(value);
    if (detail) return detail;

    const message = nonemptyString(value?.message) ?? nonemptyString(value);
    if (!message || typeof value?.message !== 'string') return message ?? fallback;

    const payload = errorPayload(value);
    return responseDetail(payload)
        ?? nonemptyString(payload?.message)
        ?? nonemptyString(payload)
        ?? message;
}

export function projectOptimizerRunPositions(tickerData, viewMode) {
    const allocationField = viewMode === 'normal' ? 'positionGmv' : 'posSizingPositionGmv';
    return (Array.isArray(tickerData) ? tickerData : [])
        .filter((row) => row?.checkBoxEnable === true)
        .map((row) => ({
            cognive_id: normalizeCogniveId(row?.cognive_id),
            sedol: row?.sedol ?? null,
            ticker: row?.ticker ?? null,
            gmv: row?.[allocationField],
            beta: row?.beta ?? null,
            exp_return: row?.exp_return ?? null,
            trade_idea: row?.trade_idea ?? null,
        }));
}

export function requireOptimizerDeleteSuccess(response) {
    if (response?.status !== 'success') {
        throw new Error(optimizerErrorMessage(
            response,
            'Optimizer portfolio deletion response was invalid.',
        ));
    }
    return response;
}

export function isDefiniteOptimizerMutationRejection(error) {
    if (error?.optimizerMutationRejected === true) return true;
    if (!error || typeof error !== 'object' || typeof error.message !== 'string') return false;
    const match = error.message.match(
        /^Cloud Run call failed:\s*(\d{3})\s+-/,
    );
    const status = error.name === 'BackendHttpError' ? error.status : match ? Number(match[1]) : null;
    return Number.isInteger(status) && status >= 400 && status < 500;
}

export function requireOptimizerPortfolioList(response) {
    if (!Array.isArray(response?.portfolios)) {
        throw new Error('Optimizer portfolio list response was invalid.');
    }
    return response.portfolios;
}

export function optimizerCommittedRefreshError(error) {
    const refreshError = new Error(optimizerErrorMessage(
        error,
        'Optimizer portfolio list refresh failed.',
    ));
    refreshError.optimizerMutationCommitted = true;
    return refreshError;
}

export async function deleteOptimizerPortfolioAndLoadList({
    portfolioId,
    deleteRequest,
    listRequest,
}) {
    requireOptimizerDeleteSuccess(await deleteRequest(portfolioId));
    try {
        const response = await listRequest();
        const portfolios = requireOptimizerPortfolioList(response);
        if (response?.optimizerMutationReconciled !== true) {
            throw new Error('Optimizer portfolio list did not reconcile the completed deletion.');
        }
        return portfolios;
    } catch (error) {
        throw optimizerCommittedRefreshError(error);
    }
}
